import { inject, Injectable } from '@angular/core';
import { map, Observable, tap } from 'rxjs';
import { Guid, once } from '@web/utils';
import { AdminBackgroundTaskApiService } from '../api/services/admin-background-task-api.service';
import { AdminBackgroundTaskStatusResponse } from '../models/admin-background-task-status.response';
import { ConfirmationService, TableDataPayload, TableLazyLoad, ToastService } from '@web/shared/ui';
import { AdminBackgroundTaskCollectionItem } from '../models/admin-background-task-collection-item';
import { CollectionRequestBuilder } from '@web/common';
import { AdminBackgroundTaskCollectionFilter } from '../models/admin-background-task-collection-filter';

@Injectable({
  providedIn: 'root',
})
export class AdminBackgroundTaskDataSourceService {
  private readonly backgroundTaskApi = inject(AdminBackgroundTaskApiService);
  private readonly collectionRequestBuilder = inject(CollectionRequestBuilder);
  private readonly confirmation = inject(ConfirmationService);
  private readonly toast = inject(ToastService);

  public fetchCollection(
    lazyLoad: TableLazyLoad<AdminBackgroundTaskCollectionFilter>,
  ): Observable<TableDataPayload<AdminBackgroundTaskCollectionItem>> {
    /* prettier-ignore */
    return this.backgroundTaskApi
      .fetchCollection(
        {
          request: this.collectionRequestBuilder
            .page(lazyLoad.page)
            .pageSize(lazyLoad.rows)
            .sorts((builder) =>
              builder.fromTableSort(lazyLoad.sort).buildAsArray(),
            )
            .filters((builder) =>
              builder
                .fromTableFilter(lazyLoad.filter)
                .appendText((builder) => builder.inFor('status').build())
                .appendText((builder) => builder.inFor('type').build())
                .appendDate((builder) => builder.rangeFor('createdAt').build())
                .appendDate((builder) => builder.rangeFor('updatedAt').build())
                .buildAsArray(),
            )
            .build()
        },
      )
      .pipe(
        map(
          (response) => new TableDataPayload(
            response.items.map(
              (item) =>
                /* prettier-ignore */
                new AdminBackgroundTaskCollectionItem({
                  id: item.id,
                  status: item.status,
                  type: item.type,
                  progress: item.progress,
                  createdAt: item.createdAt,
                  updatedAt: item.updatedAt,
                  canBeDeleted: item.contextOperations.canBeDeleted,
                }),
            ),
            response.pageSize,
            response.page,
            response.total,
          )
        ),
      );
  }

  public confirmAndRemoveById(id: Guid): Observable<void> {
    return new Observable<void>((subscriber) => {
      this.confirmation.show((builder) =>
        builder
          .title('Potwierdzenie')
          .message('Czy na pewno chcesz usunąć wybrany zadanie?')
          .accept(() =>
            this.backgroundTaskApi
              .remove(id)
              .pipe(
                tap(() =>
                  this.toast.show((builder) =>
                    builder
                      .initSuccess('Wybrany element został pomyślnie usunięty.')
                      .build(),
                  ),
                ),
                once(),
              )
              .subscribe(subscriber),
          )
          .build(),
      );
    });
  }

  public fetchStatus(params: {
    id: Guid;
  }): Observable<AdminBackgroundTaskStatusResponse> {
    const { id } = params;

    return this.backgroundTaskApi.fetchStatus({ id }).pipe(
      map(
        (response) =>
          new AdminBackgroundTaskStatusResponse({
            status: response.status,
            progress: response.progress,
            id: response.id,
            taskData: response.taskData,
          }),
      ),
    );
  }
}
